import { failure, ok, pending, Result } from "../../common/common";
import { AxiosInstance } from "axios";
import { makeAutoObservable, runInAction } from "mobx";
import { Language } from "../../i18n/languages";

import { TMessage } from "../../i18n/tt";
import { NumberFieldModel, RadioFieldModel } from "../../common/commonForms";
import { ShareType, ShareTypeCode, shareTypeLabel } from "../../common/share";

export class NewShareClassController {
  organisationNumber: string;
  shareTypeApi: ShareClassApi;
  onClose: () => void;
  shareTypeModel: RadioFieldModel<ShareTypeCode>;
  numberOfVotesModel: NumberFieldModel;
  addShareTypeResult: Result<void, TMessage> | undefined = undefined;

  constructor(
    organisationNumber: string,
    shareTypeApi: ShareClassApi,
    onCancel: () => void,
    availableShareTypes: ShareTypeCode[],
    locale: Language,
    private onFinish: (newShareType: ShareType) => void,
  ) {
    this.organisationNumber = organisationNumber;
    this.shareTypeApi = shareTypeApi;
    this.onClose = onCancel;
    this.shareTypeModel = new RadioFieldModel<ShareTypeCode>({
      required: true,
      options: availableShareTypes,
      value: availableShareTypes[0],
      idFn: (option) => option,
      labelFn: (option) => shareTypeLabel(option),
    });
    this.numberOfVotesModel = new NumberFieldModel({
      required: true,
      isPositive: true,
      locale: locale,
      maxDecimals: 5,
      value: 1,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  createShareType = async () => {
    this.validate();
    if (this.isValid() && this.numberOfVotesModel.parsedValue) {
      this.addShareTypeResult = pending();
      await this.shareTypeApi
        .createShareClass({
          organizationnumber: this.organisationNumber,
          vote: this.numberOfVotesModel.parsedValue,
          sharetypeCode: this.shareTypeModel.value,
        })
        .then((newShareType) => {
          runInAction(() => {
            this.addShareTypeResult = ok();
            if (newShareType !== undefined) {
              this.onFinish(newShareType);
            }
            this.onClose();
          });
        })
        .catch(() => {
          runInAction(() => {
            this.addShareTypeResult = failure({ key: "shareClasses.shareClassForm.save.error" });
          });
        });
    }
  };

  validate() {
    this.numberOfVotesModel.validate();
    this.shareTypeModel.validate();
  }

  isValid() {
    return this.numberOfVotesModel.isValid && this.shareTypeModel.isValid;
  }
}

export class ShareClassApi {
  private http: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.http = axiosInstance;
  }

  createShareClass(request: CreateShareClassRequest): Promise<ShareType> {
    return this.http.post(`/sharetype/new`, request).then((res) => res.data);
  }
}

export interface CreateShareClassRequest {
  organizationnumber: string;
  vote: number;
  sharetypeCode: ShareTypeCode;
}
